import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { editStudent } from '../../../api/action/user';
import { ArrowLeft, Loader2, Save, X } from 'lucide-react';
import PersonalInfo from './PersonalInfo';
import AddressDetails from './AddressDetails';
import EducationalDetails from './EducationalDetails';
import { Alert as UiAlert } from '../../../components/ui/Feedback';

const CircularProgress = ({ size = 24, className = "" }) => (
    <Loader2 className={`animate-spin ${className}`} style={{ width: size, height: size }} aria-hidden="true" />
);

const Snackbar = ({ anchorOrigin = {}, autoHideDuration, children, onClose, open }) => {
    useEffect(() => {
        if (!open || !autoHideDuration || !onClose) return undefined;
        const timer = window.setTimeout(() => onClose({}, 'timeout'), autoHideDuration);
        return () => window.clearTimeout(timer);
    }, [autoHideDuration, onClose, open]);

    if (!open) return null;

    const vertical = anchorOrigin.vertical === 'top' ? 'top-5' : 'bottom-5';
    const horizontal = anchorOrigin.horizontal === 'right'
        ? 'right-5'
        : anchorOrigin.horizontal === 'left'
            ? 'left-5'
            : 'left-1/2 -translate-x-1/2';

    return (
        <div className={`fixed ${vertical} ${horizontal} z-50 w-[calc(100%-2rem)] max-w-md`}>
            {children}
        </div>
    );
};

const Alert = ({ children, onClose, severity = 'info' }) => (
    <div className="relative">
        <UiAlert tone={severity}>{children}</UiAlert>
        {onClose && (
            <button
                type="button"
                aria-label="Close notification"
                className="absolute right-2 top-2 rounded p-1 hover:bg-black/10"
                onClick={onClose}
            >
                <X className="h-4 w-4" aria-hidden="true" />
            </button>
        )}
    </div>
);

const EditUserProfile = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const authState = useSelector((state) => state?.auth?.data);
    const [loading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [domainType, setDomainType] = useState("");

    // Initialize user state from auth state
    const [user, setUser] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        dob: '',
        addressList: [],
        educationDetails: [],
        employmentDetails: [],
        profilePicture: {},
        primaryId: '',
        accountId: '',
        createdDate: ''
    });

    // Password state
    const [passwordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });

    // Profile image upload state
    const [profileImageToUpload, setProfileImageToUpload] = useState(null);

    useEffect(() => {
        const domain = localStorage.getItem("domainType") || "EDUCATION";
        setDomainType(domain);

        if (authState) {
            const userData = authState?.user || authState;
            setUser({
                ...userData,
                firstName: userData.firstName || userData.firstname || '',
                lastName: userData.lastName || userData.lastname || '',
                email: userData.email || '',
                phoneNumber: userData.phoneNumber || userData.phone || '',
                dob: userData.dob || '',
                addressList: userData.addresses || userData.addressList || [],
                educationDetails: userData.educationDetails || userData.education || [],
                employmentDetails: userData.employmentDetails || userData.employment || [],
                profilePicture: userData.profilePicture || {},
                primaryId: userData.primaryId || '',
                accountId: userData.accountId || '',
                createdDate: userData.createdDate || userData.created || userData.createdAt || ''
            });
        }
    }, [authState]);

    // Handle general input changes
    const handleChange = (e, index, section) => {
        const { name, value } = e.target;

        if (section === 'addressList') {
            const updatedAddresses = [...user.addressList];
            updatedAddresses[index] = {
                ...updatedAddresses[index],
                [name]: value
            };
            setUser(prev => ({ ...prev, addressList: updatedAddresses }));
        } else if (section === 'educationDetails') {
            const updatedEducation = [...user.educationDetails];
            updatedEducation[index] = {
                ...updatedEducation[index],
                [name]: value
            };
            setUser(prev => ({ ...prev, educationDetails: updatedEducation }));
        } else if (section === 'employmentDetails') {
            const updatedEmployment = [...user.employmentDetails];
            updatedEmployment[index] = {
                ...updatedEmployment[index],
                [name]: value
            };
            setUser(prev => ({ ...prev, employmentDetails: updatedEmployment }));
        } else {
            setUser(prev => ({ ...prev, [name]: value }));
        }
    };

    // Handle profile image change
    const handleProfileImgChange = (imageData) => {
        setProfileImageToUpload(imageData);
        setUser(prev => ({
            ...prev,
            profilePicture: {
                ...prev.profilePicture,
                displayLink: imageData.displayLink,
                storedLink: imageData.storedLink
            }
        }));
    };

    // Add new address
    const addAddress = () => {
        setUser(prev => ({
            ...prev,
            addressList: [
                ...prev.addressList,
                {
                    type: '',
                    country: 'India',
                    streetName1: '',
                    streetName2: '',
                    city: '',
                    state: '',
                    zipcode: ''
                }
            ]
        }));
    };

    // Remove address
    const removeAddress = (index) => {
        if (user.addressList.length > 1) {
            const updatedAddresses = user.addressList.filter((_, i) => i !== index);
            setUser(prev => ({ ...prev, addressList: updatedAddresses }));
        } else {
            showSnackbar('Cannot delete the last address!', 'warning');
        }
    };

    // Add education
    const addEducation = () => {
        setUser(prev => ({
            ...prev,
            educationDetails: [
                ...prev.educationDetails,
                {
                    educationLevel: '',
                    schoolName: '',
                    degree: '',
                    fieldOfStudy: '',
                    currentlyStudying: false,
                    startDate: '',
                    endDate: ''
                }
            ]
        }));
    };

    // Remove education
    const removeEducation = (index) => {
        const updatedEducation = user.educationDetails.filter((_, i) => i !== index);
        setUser(prev => ({ ...prev, educationDetails: updatedEducation }));
    };

    // Show snackbar notification
    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            // Prepare user data for update
            const userDataToUpdate = {
                ...user,
                // Ensure required fields are included
                firstname: user.firstName,
                lastname: user.lastName,
                phone: user.phoneNumber,
                addresses: user.addressList,
                // Use educationDetails (not education) to match backend
                educationDetails: user.educationDetails,
                employment: user.employmentDetails
            };

            // If password is being updated
            if (passwordData.newPassword && passwordData.newPassword === passwordData.confirmNewPassword) {
                userDataToUpdate.password = passwordData.newPassword;
                userDataToUpdate.currentPassword = passwordData.currentPassword;
            }

            // Dispatch update action
            const result = await dispatch(editStudent(userDataToUpdate));

            if (result) {
                showSnackbar('Profile updated successfully!', 'success');
                setTimeout(() => {
                    navigate('/my-profile');
                }, 1500);
            } else {
                showSnackbar('Failed to update profile. Please try again.', 'error');
            }
        } catch {
            showSnackbar('An error occurred. Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        navigate('/my-profile');
    };

    // Close snackbar
    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 pt-20 pb-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="pt-8 pb-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={handleCancel}
                                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                <ArrowLeft aria-hidden="true" />
                            </button>
                            <div className="flex flex-col">
                                <h1 className="text-3xl font-bold text-gray-900">Edit Profile</h1>
                                <p className="text-gray-600 mt-2">Update your personal information</p>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 mt-4 sm:mt-0">
                            <button
                                onClick={handleCancel}
                                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium flex items-center space-x-2"
                            >
                                <X aria-hidden="true" />
                                <span>Cancel</span>
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 font-medium flex items-center space-x-2 disabled:opacity-50"
                            >
                                {saving ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <Save aria-hidden="true" />
                                )}
                                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Form */}
                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Personal Information */}
                    <PersonalInfo
                        user={user}
                        disabled={false}
                        handleChange={handleChange}
                        handleProfileImgChange={handleProfileImgChange}
                        profileImageToUpload={profileImageToUpload}
                    />

                    {/* Address Details */}
                    <AddressDetails
                        user={user}
                        handleChange={handleChange}
                        addAddress={addAddress}
                        removeAddress={removeAddress}
                        disabled={false}
                    />

                    {/* Education Details - Only show for EDUCATION domain */}
                    {domainType === "EDUCATION" && (
                        <EducationalDetails
                            user={user}
                            handleChange={handleChange}
                            addEducation={addEducation}
                            removeEducation={removeEducation}
                        />
                    )}
                </form>
            </div>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default EditUserProfile;
